import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { X, Sparkles, FileEdit, CheckCircle2, Trash2, Pencil, Plus, Save, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/use-auth";
import { getRoleLevel } from "@shared/roles";
import type { ChangelogEntry } from "@shared/schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

const CATEGORIES = ["UX", "Commercial", "Technique", "Organisation", "Administratif", "SAV", "Produits"] as const;

const CATEGORY_COLORS: Record<string, string> = {
  UX: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800",
  Commercial: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  Technique: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
  Organisation: "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800",
  Administratif: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700",
  SAV: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  Produits: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
};

const LS_KEY = "changelog-last-seen";

export function getChangelogUnread(entries: ChangelogEntry[]): boolean {
  const published = entries.filter(e => (e as any).statut !== "brouillon");
  if (!published.length) return false;
  try {
    const last = localStorage.getItem(LS_KEY);
    if (!last) return true;
    const lastDate = new Date(last);
    return published.some((e) => new Date(e.createdAt!) > lastDate);
  } catch {
    return false;
  }
}

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface ChangelogDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangelogDrawer({ isOpen, onClose }: ChangelogDrawerProps) {
  const { data: entries = [], isLoading } = useQuery<ChangelogEntry[]>({
    queryKey: ["/api/changelog"],
    queryFn: async () => {
      const res = await authFetch("/api/changelog");
      return res.json();
    },
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      try {
        localStorage.setItem(LS_KEY, new Date().toISOString());
      } catch {}
    }
  }, [isOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] sm:max-w-[480px] p-0 flex flex-col"
        aria-describedby="changelog-desc"
      >
        <SheetHeader className="px-6 py-5 border-b bg-card shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-base font-bold leading-tight">Nouveautés</SheetTitle>
                <p id="changelog-desc" className="text-xs text-muted-foreground mt-0.5">
                  Suivez les évolutions de la plateforme
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="shrink-0"
              data-testid="button-close-changelog"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 space-y-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm">
              Aucune nouveauté pour l'instant.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {entries.map((entry) => (
                <div key={entry.id} className="px-6 py-5 hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${CATEGORY_COLORS[entry.categorie] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                      {entry.categorie}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(entry.createdAt)}</span>
                  </div>
                  <p className="font-semibold text-sm text-foreground mb-1">{entry.titre}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{entry.contenu}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Admin Panel ─────────────────────────────────────────────────────────────

export function ChangelogAdminPanel() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { profile } = useAuth();
  const isSuperAdmin = getRoleLevel((profile as any)?.accessRole) >= 4;

  const [activeTab, setActiveTab] = useState<"publie" | "brouillon">("publie");

  const { data: entries = [], isLoading } = useQuery<ChangelogEntry[]>({
    queryKey: ["/api/changelog"],
    queryFn: async () => (await authFetch("/api/changelog")).json(),
  });

  const { data: brouillons = [], isLoading: brouillonsLoading } = useQuery<ChangelogEntry[]>({
    queryKey: ["/api/changelog/brouillons"],
    queryFn: async () => (await authFetch("/api/changelog/brouillons")).json(),
    enabled: isSuperAdmin,
  });

  const [form, setForm] = useState<{ titre: string; categorie: string; contenu: string; statut: string }>({
    titre: "", categorie: "", contenu: "", statut: "publie",
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<string | null>(null);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["/api/changelog"] });
    qc.invalidateQueries({ queryKey: ["/api/changelog/brouillons"] });
    qc.invalidateQueries({ queryKey: ["/api/changelog/brouillons/count"] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await authFetch("/api/changelog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: () => {
      invalidateAll();
      setForm({ titre: "", categorie: "", contenu: "", statut: "publie" });
      setShowForm(false);
      toast({ title: "Entrée créée" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof form }) => {
      const res = await authFetch(`/api/changelog/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: () => {
      invalidateAll();
      setEditingId(null);
      setShowForm(false);
      setForm({ titre: "", categorie: "", contenu: "", statut: "publie" });
      toast({ title: "Entrée modifiée" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setDeleting(id);
      const res = await authFetch(`/api/changelog/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message);
    },
    onSuccess: () => {
      invalidateAll();
      setDeleting(null);
      toast({ title: "Entrée supprimée" });
    },
    onError: (e: any) => { setDeleting(null); toast({ title: "Erreur", description: e.message, variant: "destructive" }); },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      setPublishing(id);
      const res = await authFetch(`/api/changelog/${id}/publier`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: () => {
      invalidateAll();
      setPublishing(null);
      toast({ title: "Entrée publiée ✓", description: "Visible par toute l'équipe." });
    },
    onError: (e: any) => { setPublishing(null); toast({ title: "Erreur", description: e.message, variant: "destructive" }); },
  });

  const startEdit = (entry: ChangelogEntry) => {
    setEditingId(entry.id);
    setForm({ titre: entry.titre, categorie: entry.categorie, contenu: entry.contenu, statut: (entry as any).statut || "publie" });
    setShowForm(true);
  };

  const startNew = () => {
    setEditingId(null);
    setForm({ titre: "", categorie: "", contenu: "", statut: "publie" });
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ titre: "", categorie: "", contenu: "", statut: "publie" });
  };

  const submit = () => {
    if (!form.titre || !form.categorie || !form.contenu) {
      toast({ title: "Champs obligatoires", description: "Remplissez tous les champs.", variant: "destructive" });
      return;
    }
    if (editingId) updateMutation.mutate({ id: editingId, data: form });
    else createMutation.mutate(form);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const displayedEntries = activeTab === "publie" ? entries : brouillons;
  const displayedLoading = activeTab === "publie" ? isLoading : brouillonsLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Changelog</h2>
          <p className="text-sm text-muted-foreground">Publiez les nouveautés visibles par toute l'équipe.</p>
        </div>
        {!showForm && (
          <Button onClick={startNew} data-testid="button-new-changelog">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle entrée
          </Button>
        )}
      </div>

      {/* Tabs publiés / brouillons */}
      {isSuperAdmin && (
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab("publie")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === "publie" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-changelog-publie"
          >
            Publiés ({entries.length})
          </button>
          <button
            onClick={() => setActiveTab("brouillon")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === "brouillon" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-changelog-brouillons"
          >
            <FileEdit className="w-3.5 h-3.5" />
            Brouillons
            {brouillons.length > 0 && (
              <span className="min-w-[18px] h-[18px] text-[10px] font-bold bg-amber-500 text-white rounded-full flex items-center justify-center px-1">
                {brouillons.length}
              </span>
            )}
          </button>
        </div>
      )}

      {showForm && (
        <div className="rounded-xl border bg-card p-5 space-y-4 shadow-sm">
          <h3 className="font-semibold">{editingId ? "Modifier l'entrée" : "Nouvelle entrée"}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cl-titre">Titre</Label>
              <Input id="cl-titre" value={form.titre} onChange={(e) => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex : Nouveau module SAV" data-testid="input-changelog-titre" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-cat">Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm(f => ({ ...f, categorie: v }))}>
                <SelectTrigger id="cl-cat" data-testid="select-changelog-categorie">
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cl-contenu">Contenu</Label>
            <Textarea
              id="cl-contenu"
              value={form.contenu}
              onChange={(e) => setForm(f => ({ ...f, contenu: e.target.value }))}
              rows={4}
              placeholder="Décrivez la nouveauté en 2-4 lignes..."
              className="resize-none"
              data-testid="textarea-changelog-contenu"
            />
          </div>
          {isSuperAdmin && (
            <div className="space-y-1.5">
              <Label htmlFor="cl-statut">Statut</Label>
              <Select value={form.statut} onValueChange={(v) => setForm(f => ({ ...f, statut: v }))}>
                <SelectTrigger id="cl-statut" data-testid="select-changelog-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="publie">Publié — visible par l'équipe</SelectItem>
                  <SelectItem value="brouillon">Brouillon — visible par super_admin uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={cancel} disabled={isSaving} data-testid="button-changelog-cancel">Annuler</Button>
            <Button onClick={submit} disabled={isSaving} data-testid="button-changelog-save">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? "Enregistrer" : form.statut === "brouillon" ? "Sauvegarder en brouillon" : "Publier"}
            </Button>
          </div>
        </div>
      )}

      {displayedLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : displayedEntries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {activeTab === "brouillon" ? "Aucun brouillon en attente." : "Aucune entrée pour l'instant."}
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="divide-y divide-border">
            {displayedEntries.map((entry) => (
              <div key={entry.id} className="flex items-start gap-4 px-5 py-4 hover:bg-secondary/30 transition-colors" data-testid={`changelog-entry-${entry.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${CATEGORY_COLORS[entry.categorie] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                      {entry.categorie}
                    </span>
                    {(entry as any).statut === "brouillon" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                        Brouillon
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDate(entry.createdAt)}</span>
                  </div>
                  <p className="font-semibold text-sm">{entry.titre}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{entry.contenu}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isSuperAdmin && (entry as any).statut === "brouillon" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-emerald-600"
                      onClick={() => publishMutation.mutate(entry.id)}
                      disabled={publishing === entry.id}
                      title="Publier"
                      data-testid={`button-publish-changelog-${entry.id}`}
                    >
                      {publishing === entry.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => startEdit(entry)} data-testid={`button-edit-changelog-${entry.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMutation.mutate(entry.id)}
                    disabled={deleting === entry.id}
                    data-testid={`button-delete-changelog-${entry.id}`}
                  >
                    {deleting === entry.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
